import React, { useState, useEffect } from 'react';
import { Palette, Copy, RefreshCw } from 'lucide-react';

const ColorShadeGeneratorTool = () => {
  const [baseColor, setBaseColor] = useState('#3B82F6');
  const [shades, setShades] = useState([]);
  const [copied, setCopied] = useState('');
  const [format, setFormat] = useState('hex'); // hex, rgb, hsl

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
  };

  const hslToRgb = (h, s, l) => {
    s /= 100; l /= 100;
    const k = n => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return {
      r: Math.round(255 * f(0)),
      g: Math.round(255 * f(8)),
      b: Math.round(255 * f(4))
    };
  };

  const generateShades = () => {
    const rgb = hexToRgb(baseColor);
    if (!rgb) return;

    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    const newShades = [];

    // Generate 11 shades (50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950)
    const lightnesses = [97, 93, 86, 77, 66, 55, 44, 33, 22, 13, 7];
    const names = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];

    lightnesses.forEach((l, i) => {
      const newRgb = hslToRgb(hsl.h, hsl.s, l);
      const hex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
      const newHsl = { h: hsl.h, s: hsl.s, l };
      
      newShades.push({
        name: names[i],
        hex,
        rgb: newRgb,
        hsl: newHsl
      });
    });

    setShades(newShades);
  };

  useEffect(() => {
    generateShades();
  }, [baseColor]);

  const getColorValue = (shade) => {
    switch (format) {
      case 'rgb':
        return `rgb(${shade.rgb.r}, ${shade.rgb.g}, ${shade.rgb.b})`;
      case 'hsl':
        return `hsl(${shade.hsl.h}, ${shade.hsl.s}%, ${shade.hsl.l}%)`;
      default:
        return shade.hex;
    }
  };

  const copyColor = (shade, key) => {
    navigator.clipboard.writeText(getColorValue(shade));
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const copyAllAsCss = () => {
    const css = shades.map(shade => 
      `--color-${shade.name}: ${getColorValue(shade)};`
    ).join('\n');
    navigator.clipboard.writeText(css);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const getContrastColor = (hex) => {
    const rgb = hexToRgb(hex);
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const presetColors = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', 
    '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', 
    '#A855F7', '#EC4899', '#F43F5E'
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Base Color</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={baseColor}
              onChange={(e) => setBaseColor(e.target.value)}
              className="w-12 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={baseColor}
              onChange={(e) => {
                if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                  setBaseColor(e.target.value);
                }
              }}
              className="w-28 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="hex">HEX</option>
            <option value="rgb">RGB</option>
            <option value="hsl">HSL</option>
          </select>
        </div>

        <button
          onClick={copyAllAsCss}
          className={`px-4 py-2 rounded-lg transition-colors ${
            copied === 'all' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          {copied === 'all' ? 'Copied!' : 'Copy as CSS Variables'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {presetColors.map((color) => (
          <button
            key={color}
            onClick={() => setBaseColor(color)}
            className="w-8 h-8 rounded-full border-2 border-gray-600 hover:border-white transition-colors"
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-11 gap-2">
        {shades.map((shade) => (
          <div
            key={shade.name}
            className="relative group cursor-pointer"
            onClick={() => copyColor(shade, shade.name)}
          >
            <div
              className="h-20 rounded-lg flex items-end p-2 transition-transform hover:scale-105"
              style={{ backgroundColor: shade.hex }}
            >
              <span 
                className="text-xs font-medium"
                style={{ color: getContrastColor(shade.hex) }}
              >
                {shade.name}
              </span>
            </div>
            <div className={`absolute inset-0 flex items-center justify-center rounded-lg transition-opacity ${
              copied === shade.name ? 'opacity-100 bg-black/50' : 'opacity-0 group-hover:opacity-100 bg-black/30'
            }`}>
              <span className="text-white text-xs font-medium">
                {copied === shade.name ? 'Copied!' : 'Click to copy'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg overflow-x-auto">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Color Values</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-left">
              <th className="pb-2 pr-4">Shade</th>
              <th className="pb-2 pr-4">HEX</th>
              <th className="pb-2 pr-4">RGB</th>
              <th className="pb-2">HSL</th>
            </tr>
          </thead>
          <tbody className="font-mono text-xs">
            {shades.map((shade) => (
              <tr key={shade.name} className="border-t border-gray-700">
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded" 
                      style={{ backgroundColor: shade.hex }}
                    />
                    {shade.name}
                  </div>
                </td>
                <td className="py-2 pr-4 text-gray-300">{shade.hex}</td>
                <td className="py-2 pr-4 text-gray-300">
                  rgb({shade.rgb.r}, {shade.rgb.g}, {shade.rgb.b})
                </td>
                <td className="py-2 text-gray-300">
                  hsl({shade.hsl.h}, {shade.hsl.s}%, {shade.hsl.l}%)
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ColorShadeGeneratorTool;
